import type { DraftEvents } from "@/lib/database/events";
import { INITIAL_FORM_DATA } from "../_hooks/use-wizard-state";
import type { CreateEventFormData } from "../_types/wizard-types";
import { BUDGET_CATEGORY_FIELDS, EMPTY_MEETING_SPACE_SCHEDULE } from "./constants";
import { hydrateFormDataFromDraft } from "./draft-actions";

/**
 * Answers a fresh wizard starts with, carried over from the signed-in user's last submitted event.
 * Only answers that tend to repeat across a team's events come along. The name, dates, agenda,
 * notes and documents describe one event and stay blank. Room needs, meeting-space schedules and
 * meal services come without their dates, which the new event's own steps re-derive.
 *
 * @param previousEvent - The user's most recent submitted draft (`event_id` set), or `null`.
 * @returns The fields to start from, or `null` when there is nothing worth filling in.
 */
export function buildFreshStartPrefill(previousEvent: DraftEvents | null): Partial<CreateEventFormData> | null {
  if (!previousEvent) return null;
  const previous = { ...INITIAL_FORM_DATA, ...hydrateFormDataFromDraft(previousEvent) };

  const prefill: Partial<CreateEventFormData> = {
    numPeople: previous.numPeople,
    accommodations: { ...INITIAL_FORM_DATA.accommodations, needed: previous.accommodations.needed },
    meetingRooms: {
      ...previous.meetingRooms,
      dates: INITIAL_FORM_DATA.meetingRooms.dates,
      generalSession: { ...previous.meetingRooms.generalSession, schedule: EMPTY_MEETING_SPACE_SCHEDULE },
      breakout: { ...previous.meetingRooms.breakout, schedule: EMPTY_MEETING_SPACE_SCHEDULE },
    },
    foodBeverage: {
      ...previous.foodBeverage,
      meals: Object.fromEntries(Object.keys(previous.foodBeverage.meals).map((meal) => [meal, []])),
    },
    budgetUnknown: previous.budgetUnknown,
    ...Object.fromEntries(Object.values(BUDGET_CATEGORY_FIELDS).map((field) => [field, previous[field]])),
    budgetCurrency: previous.budgetCurrency,
    decisionFactors: previous.decisionFactors,
    decisionFactorOtherText: previous.decisionFactorOtherText,
    additionalServices: previous.additionalServices,
    customQuestions: previous.customQuestions,
    customConcessions: previous.customConcessions,
  };

  const isAllBlank = JSON.stringify(prefill) === JSON.stringify(blankOut(prefill));
  return isAllBlank ? null : prefill;
}

/**
 * The blank values for exactly the fields a prefill set, for "Start blank".
 *
 * @param prefill - The prefill returned by {@link buildFreshStartPrefill}.
 * @returns The same keys, each reset to its initial wizard value.
 */
export function blankOut(prefill: Partial<CreateEventFormData>): Partial<CreateEventFormData> {
  return Object.fromEntries(
    Object.keys(prefill).map((key) => [key, INITIAL_FORM_DATA[key as keyof CreateEventFormData]])
  );
}
